import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SectionEditor from '@/components/SectionEditor';
import { Award, Shield, CheckCircle, TrendingUp, Users, Star, Target, Zap, Plus, Trash2, Edit2, Upload, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { applyStyle } from '@/utils/styleHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BouncingLogos({ logos, user, onManage }) {
  const hasLogos = logos && logos.length > 0;

  return (
    <section className="py-20 overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-12" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
          <span className="badge mb-4">TRUSTED BY INDUSTRY LEADERS</span>
          <h2 className="text-3xl lg:text-5xl font-black text-foreground mb-4 tracking-tighter">Our Valued Clients</h2>
          <p className="text-muted-foreground mb-4">Packaging partners across diverse industries</p>
          {user?.role === 'admin' && (
            <Button onClick={onManage} variant="outline" size="sm" className="border-primary/30 text-primary hover:bg-primary/10 rounded-xl" data-testid="manage-logos-btn">
              <Edit2 className="w-4 h-4 mr-2" /> Manage Client Logos
            </Button>
          )}
        </motion.div>
      </div>

      {hasLogos && (
        <>
          {/* Marquee Row 1 */}
          <div className="relative mb-6">
            <div className="absolute left-0 top-0 bottom-0 w-32 z-10" style={{ background: 'linear-gradient(90deg, hsl(var(--background)), transparent)' }}></div>
            <div className="absolute right-0 top-0 bottom-0 w-32 z-10" style={{ background: 'linear-gradient(270deg, hsl(var(--background)), transparent)' }}></div>
            <motion.div className="flex gap-6" animate={{ x: ['0%', '-50%'] }} transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}>
              {[...logos, ...logos].map((item, index) => (
                <motion.div key={`row1-${item.id}-${index}`} className="flex-shrink-0 px-6 py-4 rounded-xl border border-border/20 bg-card/60 backdrop-blur-sm flex items-center gap-3"
                  animate={{ y: [0, -8, 0, 8, 0] }}
                  transition={{ duration: 3 + (index % 3), repeat: Infinity, ease: 'easeInOut', delay: index * 0.2 }}
                  whileHover={{ scale: 1.1, borderColor: 'hsl(var(--primary) / 0.5)' }}
                >
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className="w-10 h-10 object-contain rounded" />
                  ) : null}
                  <span className="text-lg font-black text-foreground/70 whitespace-nowrap tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.name}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Marquee Row 2 - reverse */}
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-32 z-10" style={{ background: 'linear-gradient(90deg, hsl(var(--background)), transparent)' }}></div>
            <div className="absolute right-0 top-0 bottom-0 w-32 z-10" style={{ background: 'linear-gradient(270deg, hsl(var(--background)), transparent)' }}></div>
            <motion.div className="flex gap-6" animate={{ x: ['-50%', '0%'] }} transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}>
              {[...logos.slice().reverse(), ...logos.slice().reverse()].map((item, index) => (
                <motion.div key={`row2-${index}`} className="flex-shrink-0 px-6 py-4 rounded-xl border border-primary/10 bg-primary/5 flex items-center gap-3"
                  animate={{ y: [0, 8, 0, -8, 0] }}
                  transition={{ duration: 2.5 + (index % 4), repeat: Infinity, ease: 'easeInOut', delay: index * 0.15 }}
                  whileHover={{ scale: 1.1, borderColor: 'hsl(var(--primary) / 0.5)' }}
                >
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className="w-10 h-10 object-contain rounded" />
                  ) : null}
                  <span className="text-lg font-black whitespace-nowrap tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-light)))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{item.name}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </>
      )}
    </section>
  );
}

export default function BrandsPage({ user, setUser, theme, toggleTheme, accentColor, onAccentChange }) {
  const [content, setContent] = useState({
    hero: { badge: 'QUALITY & CERTIFICATIONS', title: 'Our Standards', description: 'Committed to excellence through certified quality management systems and rigorous manufacturing standards' },
    iso: { title: 'ISO 9001:2015', subtitle: 'Quality Management Systems', description: 'Certified for maintaining consistent quality in manufacturing.' },
    quality: { badge: 'OUR COMMITMENT', title: 'Quality Standards' },
    testimonials: { badge: 'TESTIMONIALS', title: 'What Clients Say' },
  });
  const [clientLogos, setClientLogos] = useState([]);
  const [showLogoDialog, setShowLogoDialog] = useState(false);
  const [logoForm, setLogoForm] = useState({ name: '', image_url: '' });
  const [editingLogoId, setEditingLogoId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [showTestimonialDialog, setShowTestimonialDialog] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({ quote: '', author: '', company: '', rating: 5 });

  const loadPageContent = useCallback(async () => {
    try { const r = await axios.get(`${API}/pages/brands`); if (r.data?.content) setContent(p => ({ ...p, ...r.data.content })); } catch (error) { console.error('Failed to load page content:', error); }
  }, []);

  const updateSection = (key) => (data) => setContent(p => ({ ...p, [key]: data }));

  const loadClientLogos = useCallback(async () => {
    try { const r = await axios.get(`${API}/client-logos`); setClientLogos(r.data); } catch (error) { console.error('Failed to load client logos:', error); }
  }, []);

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Upload an image'); return; }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setLogoForm({ ...logoForm, image_url: r.data.url.startsWith('http') ? r.data.url : `${BACKEND_URL}${r.data.url}` });
      toast.success('Uploaded!');
    } catch (error) { console.error('Upload failed:', error); toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSaveLogo = async () => {
    if (!logoForm.name.trim()) { toast.error('Enter client name'); return; }
    const token = localStorage.getItem('token');
    try {
      if (editingLogoId) {
        await axios.put(`${API}/client-logos/${editingLogoId}`, logoForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Logo updated!');
      } else {
        await axios.post(`${API}/client-logos`, { ...logoForm, order: clientLogos.length }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Logo added!');
      }
      setShowLogoDialog(false);
      setLogoForm({ name: '', image_url: '' });
      setEditingLogoId(null);
      loadClientLogos();
    } catch (error) { console.error('Save failed:', error); toast.error('Failed to save'); }
  };

  const handleDeleteLogo = async (id) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/client-logos/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Logo deleted!');
      loadClientLogos();
    } catch (error) { console.error('Delete failed:', error); toast.error('Failed to delete'); }
  };

  const openEditLogo = (logo) => {
    setLogoForm({ name: logo.name, image_url: logo.image_url });
    setEditingLogoId(logo.id);
    setShowLogoDialog(true);
  };

  const openAddLogo = () => {
    setLogoForm({ name: '', image_url: '' });
    setEditingLogoId(null);
    setShowLogoDialog(true);
  };

  // === Testimonials CRUD ===
  const loadTestimonials = useCallback(async () => {
    try { const r = await axios.get(`${API}/testimonials`); setTestimonials(r.data); } catch (error) { console.error('Failed to load testimonials:', error); }
  }, []);

  useEffect(() => { loadPageContent(); loadClientLogos(); loadTestimonials(); }, [loadPageContent, loadClientLogos, loadTestimonials]);

  const openAddTestimonial = () => {
    setTestimonialForm({ quote: '', author: '', company: '', rating: 5 });
    setEditingTestimonial(null);
    setShowTestimonialDialog(true);
  };
  const openEditTestimonial = (t) => {
    setTestimonialForm({ quote: t.quote, author: t.author, company: t.company, rating: t.rating });
    setEditingTestimonial(t);
    setShowTestimonialDialog(true);
  };
  const handleSaveTestimonial = async () => {
    if (!testimonialForm.quote.trim() || !testimonialForm.author.trim()) { toast.error('Quote and Author are required'); return; }
    const token = localStorage.getItem('token');
    try {
      if (editingTestimonial) {
        await axios.put(`${API}/testimonials/${editingTestimonial.id}`, testimonialForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Testimonial updated!');
      } else {
        await axios.post(`${API}/testimonials`, testimonialForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Testimonial added!');
      }
      setShowTestimonialDialog(false);
      loadTestimonials();
    } catch (error) { console.error('Save failed:', error); toast.error('Failed to save'); }
  };
  const handleDeleteTestimonial = async (id) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/testimonials/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Testimonial deleted!');
      loadTestimonials();
    } catch (error) { console.error('Delete failed:', error); toast.error('Failed to delete'); }
  };

  const qualityStandards = [
    { icon: Shield, title: 'Quality Assurance', description: 'Rigorous quality control at every stage.' },
    { icon: CheckCircle, title: 'Material Testing', description: 'Comprehensive material durability testing.' },
    { icon: Target, title: 'Precision Manufacturing', description: 'Accurate specifications guaranteed.' },
    { icon: Zap, title: 'Fast Delivery', description: 'Efficient processes, tight deadlines.' },
  ];

  const achievements = [
    { icon: Users, number: '500+', title: 'Happy Clients' },
    { icon: Star, number: '250K+', title: 'Boxes Delivered' },
    { icon: TrendingUp, number: '98%', title: 'Satisfaction Rate' },
    { icon: Award, number: '25+', title: 'Years Experience' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={onAccentChange} />

      {/* Hero */}
      <section className="relative py-28 overflow-hidden group">
        <div className="gradient-mesh absolute inset-0"></div>
        <div className="subtle-grid absolute inset-0 opacity-20"></div>
        <SectionEditor user={user} sectionTitle="Hero" fields={[
          { key: 'badge', label: 'Badge', type: 'text', styled: true },
          { key: 'title', label: 'Title', type: 'title', styled: true },
          { key: 'description', label: 'Description', type: 'text', multiline: true, styled: true },
        ]} content={content.hero} pageName="brands" sectionKey="hero" onSave={updateSection('hero')} />
        <motion.div className="text-center px-4 relative z-10 max-w-4xl mx-auto" initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
          <span className="badge mb-6" style={applyStyle(content.hero?.badgeStyle)}>{content.hero.badge}</span>
          <h1 className="text-5xl sm:text-6xl lg:text-8xl font-black text-foreground mb-6 tracking-tighter" style={applyStyle(content.hero?.titleStyle)} data-testid="brands-title">{content.hero.title}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto" style={applyStyle(content.hero?.descriptionStyle)}>{content.hero.description}</p>
        </motion.div>
      </section>

      {/* Bouncing Client Logos */}
      <BouncingLogos logos={clientLogos} user={user} onManage={() => setShowLogoDialog(true)} />

      {/* ISO Cert */}
      <section className="py-24 relative group">
        <SectionEditor user={user} sectionTitle="ISO Certification" fields={[
          { key: 'title', label: 'Title', type: 'title', styled: true },
          { key: 'subtitle', label: 'Subtitle', type: 'text', styled: true },
          { key: 'description', label: 'Description', type: 'text', multiline: true, styled: true },
        ]} content={content.iso} pageName="brands" sectionKey="iso" onSave={updateSection('iso')} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Card className="elegant-card max-w-4xl mx-auto" data-testid="iso-certification">
              <CardContent className="p-12">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <motion.div className="w-32 h-32 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))' }} whileHover={{ rotate: 360, scale: 1.1 }} transition={{ duration: 0.8 }}>
                    <Award className="w-16 h-16 text-primary-foreground" />
                  </motion.div>
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="text-4xl font-black text-foreground mb-3" style={applyStyle(content.iso?.titleStyle)}>{content.iso?.title || 'ISO 9001:2015'}</h2>
                    <p className="text-xl font-semibold mb-4" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-light)))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', ...applyStyle(content.iso?.subtitleStyle) }}>{content.iso?.subtitle || 'Quality Management Systems'}</p>
                    <p className="text-muted-foreground leading-relaxed" style={applyStyle(content.iso?.descriptionStyle)}>{content.iso?.description || 'Certified for maintaining consistent quality in manufacturing.'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-24 relative group">
        <div className="gradient-radial-glow absolute inset-0"></div>
        <SectionEditor user={user} sectionTitle="Quality Standards" fields={[
          { key: 'badge', label: 'Badge', type: 'text', styled: true },
          { key: 'title', label: 'Title', type: 'title', styled: true },
        ]} content={content.quality} pageName="brands" sectionKey="quality" onSave={updateSection('quality')} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="badge mb-4" style={applyStyle(content.quality?.badgeStyle)}>{content.quality?.badge || 'OUR COMMITMENT'}</span>
            <h2 className="text-4xl lg:text-5xl font-black text-foreground mb-4 tracking-tight" style={applyStyle(content.quality?.titleStyle)}>{content.quality?.title || 'Quality Standards'}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualityStandards.map((s, i) => { const Icon = s.icon; return (
              <motion.div key={i} className="feature-card text-center" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} viewport={{ once: true }}>
                <motion.div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-xl mb-4" whileHover={{ scale: 1.1, rotate: 5 }}>
                  <Icon className="w-8 h-8 text-primary" />
                </motion.div>
                <h3 className="text-xl font-bold text-foreground mb-3">{s.title}</h3>
                <p className="text-muted-foreground text-sm">{s.description}</p>
              </motion.div>
            ); })}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'hsl(var(--primary))' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {achievements.map((a, i) => { const Icon = a.icon; return (
              <motion.div key={i} className="text-center" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }}>
                <motion.div className="inline-flex items-center justify-center w-16 h-16 bg-primary-foreground rounded-xl mb-4" whileHover={{ scale: 1.2, rotate: 360 }} transition={{ duration: 0.6 }}>
                  <Icon className="w-8 h-8 text-primary" />
                </motion.div>
                <p className="text-4xl font-black text-primary-foreground mb-1 font-mono-spec">{a.number}</p>
                <p className="text-sm text-primary-foreground/70">{a.title}</p>
              </motion.div>
            ); })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 relative group">
        <SectionEditor user={user} sectionTitle="Testimonials" fields={[
          { key: 'badge', label: 'Badge', type: 'text', styled: true },
          { key: 'title', label: 'Title', type: 'title', styled: true },
        ]} content={content.testimonials} pageName="brands" sectionKey="testimonials" onSave={updateSection('testimonials')} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="badge mb-4" style={applyStyle(content.testimonials?.badgeStyle)}>{content.testimonials?.badge || 'TESTIMONIALS'}</span>
            <h2 className="text-4xl lg:text-5xl font-black text-foreground mb-4 tracking-tight" style={applyStyle(content.testimonials?.titleStyle)}>{content.testimonials?.title || 'What Clients Say'}</h2>
            {user?.role === 'admin' && (
              <Button onClick={openAddTestimonial} className="mt-4 rounded-xl" data-testid="add-testimonial-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Testimonial
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div key={t.id || i} className="elegant-card p-8 relative group/card" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }} viewport={{ once: true }} data-testid={`testimonial-card-${i}`}>
                {user?.role === 'admin' && (
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity z-10">
                    <Button size="icon" variant="ghost" className="w-7 h-7 rounded-lg bg-card/80 backdrop-blur-sm hover:bg-primary/20" onClick={() => openEditTestimonial(t)} data-testid={`edit-testimonial-${i}`}>
                      <Edit2 className="w-3.5 h-3.5 text-primary" />
                    </Button>
                    <Button size="icon" variant="ghost" className="w-7 h-7 rounded-lg bg-card/80 backdrop-blur-sm hover:bg-destructive/20" onClick={() => handleDeleteTestimonial(t.id)} data-testid={`delete-testimonial-${i}`}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </div>
                )}
                <div className="flex mb-4">{[...Array(t.rating || 5)].map((_, j) => <Star key={j} className="w-5 h-5 text-primary fill-current" />)}</div>
                <p className="text-muted-foreground mb-6 italic leading-relaxed">"{t.quote}"</p>
                <div className="border-t border-border/30 pt-4">
                  <p className="font-semibold text-foreground">{t.author}</p>
                  <p className="text-sm text-muted-foreground">{t.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
          {testimonials.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-lg">No testimonials yet.</p>
              {user?.role === 'admin' && <p className="text-sm mt-2">Click "Add Testimonial" to create one.</p>}
            </div>
          )}
        </div>
      </section>

      <Footer user={user} setUser={setUser} />

      {/* Client Logo Management Dialog */}
      <Dialog open={showLogoDialog} onOpenChange={setShowLogoDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col bg-card border-border/30" data-testid="logo-management-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">{editingLogoId ? 'Edit Client Logo' : 'Manage Client Logos'}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">Upload and manage client logos for the Brands page.</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 py-4 space-y-4">
            {/* Add / Edit Form */}
            <div className="p-4 bg-background rounded-xl border border-border/30 space-y-3">
              <h3 className="text-sm font-bold text-foreground">{editingLogoId ? 'Edit Logo' : 'Add New Logo'}</h3>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">Client Name *</label>
                <Input value={logoForm.name} onChange={(e) => setLogoForm({...logoForm, name: e.target.value})} placeholder="Client name" className="bg-card border-border/30 text-foreground" data-testid="logo-name-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">Logo Image</label>
                <div className="border-2 border-dashed border-border/30 rounded-xl p-3 text-center hover:border-primary/40 transition-colors">
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" disabled={uploading} />
                  <label htmlFor="logo-upload" className="cursor-pointer flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    {uploading ? <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div> : <Upload className="w-4 h-4" />}
                    {uploading ? 'Uploading...' : 'Upload logo (any shape)'}
                  </label>
                </div>
                {logoForm.image_url && (
                  <div className="mt-2 flex items-center gap-3">
                    <img src={logoForm.image_url} alt="Logo" className="w-12 h-12 object-contain rounded border border-border/30" />
                    <button onClick={() => setLogoForm({...logoForm, image_url: ''})} className="text-xs text-destructive hover:underline">Remove</button>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveLogo} className="flex-1 rounded-xl font-bold text-primary-foreground" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))' }} data-testid="save-logo-btn">
                  {editingLogoId ? 'Update Logo' : 'Add Logo'}
                </Button>
                {editingLogoId && (
                  <Button variant="outline" onClick={() => { setEditingLogoId(null); setLogoForm({ name: '', image_url: '' }); }} className="border-border/30 rounded-xl">Cancel</Button>
                )}
              </div>
            </div>

            {/* Existing Logos */}
            {clientLogos.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-bold text-foreground">Current Logos ({clientLogos.length})</h3>
                {clientLogos.map((logo) => (
                  <div key={logo.id} className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border/30" data-testid={`logo-item-${logo.id}`}>
                    {logo.image_url ? (
                      <img src={logo.image_url} alt={logo.name} className="w-10 h-10 object-contain rounded" />
                    ) : (
                      <div className="w-10 h-10 bg-primary/10 rounded flex items-center justify-center text-xs font-bold text-primary">{logo.name.charAt(0)}</div>
                    )}
                    <span className="flex-1 font-medium text-foreground text-sm">{logo.name}</span>
                    <Button size="sm" variant="ghost" onClick={() => openEditLogo(logo)} className="h-8 w-8 p-0"><Edit2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteLogo(logo.id)} className="h-8 w-8 p-0 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
              </div>
            )}

            {!editingLogoId && clientLogos.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-4">No client logos added yet. Add your first one above!</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Testimonial Add/Edit Dialog */}
      <Dialog open={showTestimonialDialog} onOpenChange={setShowTestimonialDialog}>
        <DialogContent className="max-w-md bg-card border-border/30" data-testid="testimonial-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">{editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {editingTestimonial ? 'Update this client testimonial.' : 'Add a new client testimonial.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-medium text-foreground mb-1 block">Quote *</label>
              <Textarea
                value={testimonialForm.quote}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, quote: e.target.value })}
                placeholder="What the client said..."
                className="bg-background border-border/30 text-foreground min-h-[100px]"
                data-testid="testimonial-quote-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">Author Name *</label>
                <Input
                  value={testimonialForm.author}
                  onChange={(e) => setTestimonialForm({ ...testimonialForm, author: e.target.value })}
                  placeholder="Rajesh Kumar"
                  className="bg-background border-border/30 text-foreground"
                  data-testid="testimonial-author-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">Company / Role</label>
                <Input
                  value={testimonialForm.company}
                  onChange={(e) => setTestimonialForm({ ...testimonialForm, company: e.target.value })}
                  placeholder="E-commerce Retailer"
                  className="bg-background border-border/30 text-foreground"
                  data-testid="testimonial-company-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-foreground mb-1.5 block">Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setTestimonialForm({ ...testimonialForm, rating: n })}
                    className="p-0.5 transition-transform hover:scale-110"
                    data-testid={`testimonial-rating-${n}`}
                  >
                    <Star className={`w-7 h-7 ${n <= testimonialForm.rating ? 'text-primary fill-current' : 'text-muted-foreground/30'}`} />
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSaveTestimonial}
                className="flex-1 rounded-xl font-bold text-primary-foreground"
                style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))' }}
                data-testid="save-testimonial-btn"
              >
                {editingTestimonial ? 'Update' : 'Add Testimonial'}
              </Button>
              <Button variant="outline" onClick={() => setShowTestimonialDialog(false)} className="border-border/30 rounded-xl">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
